import { translate } from '$i18n/i18n'
import { Salutation } from './enums'
import { boolean, mixed, object, string } from 'yup'
import { hashString } from '$utils/utils.svelte'
import type { AddressData } from './addressData'
import { addressSchema, copyAddressData, DefaultAddressData } from './addressData'

export type PersonAndCompanyData = {
	id: string
	salutation: Salutation
	title: string
	firstName: string
	lastName: string
	email?: string
	phone?: string
	companyName: string
	address: AddressData
	link?: string
	onboardingComment?: string
	text: string
	showCompany: boolean
}

export function DefaultPersonAndCompanyData(): PersonAndCompanyData {
	return {
		id: '',
		email: null,
		firstName: null,
		lastName: null,
		phone: null,
		salutation: null,
		title: null,
		companyName: null,
		address: DefaultAddressData(),
		link: null,
		text: '',
		showCompany: false
	}
}

export function copyPersonAndCompanyData(personAndCompanyData, regenerateId = false) {
	if (!personAndCompanyData) return null
	const copy = DefaultPersonAndCompanyData()
	Object.assign(copy, personAndCompanyData)
	copy.address = copyAddressData(personAndCompanyData.address)
	if (regenerateId && !copy.id) {
		copy.id = generatePersonAndCompanyDataId(copy)
	}
	return copy
}

function generatePersonAndCompanyDataId(personAndCompanyData: PersonAndCompanyData) {
	const str =
		personAndCompanyData.salutation +
		personAndCompanyData.title +
		personAndCompanyData.firstName +
		personAndCompanyData.lastName +
		personAndCompanyData.email +
		personAndCompanyData.phone +
		personAndCompanyData.companyName +
		personAndCompanyData.address.street +
		personAndCompanyData.address.zip +
		personAndCompanyData.address.city
	return hashString(str)
}

export const personAndCompanySchema = object({
	id: string().notRequired(),
	salutation: mixed().when('lastName', {
		is: (lastName: string) => lastName && lastName.length > 0,
		// oxlint-disable-next-line unicorn/no-thenable
		then: (schema) =>
			schema
				.required(translate('de', 'UI.validation.person.salutation.required'))
				.oneOf(Object.values(Salutation), ({ value }) =>
					translate('de', 'UI.validation.person.salutation.oneOf', { value: String(value) })
				),
		otherwise: (schema) => schema.notRequired()
	}),
	title: string().notRequired(),
	firstName: string().notRequired(),
	companyName: string().notRequired(),
	address: object().when('companyName', {
		is: (companyName: string) => companyName && companyName.length > 0,
		// oxlint-disable-next-line unicorn/no-thenable
		then: (_schema) => addressSchema.required(translate('de', 'UI.validation.address.required')),
		otherwise: (schema) => schema.notRequired()
	}),
	lastName: string().notRequired(),
	email: string().when('companyName', {
		is: (companyName: string) => companyName && companyName.length > 0,
		// oxlint-disable-next-line unicorn/no-thenable
		then: (schema) =>
			schema
				.required(translate('de', 'UI.validation.personData.email.required'))
				.email(translate('de', 'UI.validation.personData.email.format')),
		otherwise: (schema) => schema.notRequired()
	}),
	phone: string().notRequired(),
	link: string().notRequired(),
	onboardingComment: string().notRequired(),
	showCompany: boolean().required()
	// text: string().notRequired() not needed for server
})
	.test('person-or-link', translate('de', 'UI.validation.personOrLink.required'), (test) => {
		return !((!test.lastName || !test.email) && !test.link)
	})
	.test('company-or-link', translate('de', 'UI.validation.personOrLink.required'), (test) => {
		return (
			!test.showCompany || !((!test.companyName || !Object.keys(test.address).length) && !test.link)
		)
	})
